/**
 * Hikvision IP video intercom catalog — DS-KH series (indoor monitors)
 * + DS-KD/DS-KV series (outdoor door stations).
 *
 * All specs verified against hikvision.com product pages on 2026-05-15.
 * URL cited per entry. Missing specs left undefined.
 *
 * Hikvision intercoms use Hik-Connect proprietary protocol → `type:
 * 'ip-proprietary'`. None of the verified models claim full standard
 * SIP V2 compliance.
 *
 * accessControl mapping: source `mifare` and `keypad` map directly to
 * the schema enum; other Hikvision auth methods (fingerprint, face)
 * not present on the verified KV-series here.
 */

import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const HIKVISION_INTERCOM_CATALOG: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────
  // Indoor monitors — DS-KH series (7" Pro + 10" Ultra)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'hikvision-ds-kh6320-wte1',
    manufacturer: 'Hikvision',
    model: 'DS-KH6320-WTE1',
    category: 'intercom',
    tagline: '7-inch capacitive touch IP indoor station with Wi-Fi and Hik-Connect',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/en/products/Video-Intercom-Products/IP-Series/Pro-Series/ds-kh6320-wte1/',
    specs: { type: 'ip-proprietary', side: 'indoor-monitor', monitorInches: 7, poe: true },
  },

  {
    id: 'hikvision-ds-kh6320-te1',
    manufacturer: 'Hikvision',
    model: 'DS-KH6320-TE1',
    category: 'intercom',
    tagline: '7-inch IP indoor station, PoE, no Wi-Fi',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/en/products/Video-Intercom-Products/IP-Series/Pro-Series/ds-kh6320-te1/',
    specs: { type: 'ip-proprietary', side: 'indoor-monitor', monitorInches: 7, poe: true },
  },

  {
    id: 'hikvision-ds-kh6320-tde1',
    manufacturer: 'Hikvision',
    model: 'DS-KH6320-TDE1',
    category: 'intercom',
    tagline: '7-inch hybrid IP/4-wire indoor station with PoE',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/en/products/Video-Intercom-Products/IP-Series/Pro-Series/ds-kh6320-tde1/',
    specs: { type: 'ip-proprietary', side: 'indoor-monitor', monitorInches: 7, poe: true },
  },

  {
    id: 'hikvision-ds-kh6320-wte2',
    manufacturer: 'Hikvision',
    model: 'DS-KH6320-WTE2',
    category: 'intercom',
    tagline: '7-inch 2-wire IP indoor station with Wi-Fi',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/en/products/Video-Intercom-Products/2-wire-Series/Pro-Series/ds-kh6320-wte2/',
    specs: { type: 'audio-video-2wire', side: 'indoor-monitor', monitorInches: 7, poe: false },
  },

  {
    id: 'hikvision-ds-kh6350-wte1',
    manufacturer: 'Hikvision',
    model: 'DS-KH6350-WTE1',
    category: 'intercom',
    tagline: '7-inch IP indoor station with Hik-Connect mobile control',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/en/products/Video-Intercom-Products/IP-Series/Pro-Series/ds-kh6350-wte1-white/',
    specs: { type: 'ip-proprietary', side: 'indoor-monitor', monitorInches: 7, poe: true },
  },

  {
    id: 'hikvision-ds-kh8350-wte1',
    manufacturer: 'Hikvision',
    model: 'DS-KH8350-WTE1',
    category: 'intercom',
    tagline: 'Ultra-slim 7-inch IP indoor station, Wi-Fi, Reddot award',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/en/products/Video-Intercom-Products/IP-Series/Pro-Series/ds-kh8350-wte1/',
    specs: { type: 'ip-proprietary', side: 'indoor-monitor', monitorInches: 7, poe: true },
  },

  {
    id: 'hikvision-ds-kh8350-te1',
    manufacturer: 'Hikvision',
    model: 'DS-KH8350-TE1',
    category: 'intercom',
    tagline: '7-inch IP indoor station, PoE, no Wi-Fi',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/en/products/Video-Intercom-Products/IP-Series/Pro-Series/ds-kh8350-te1/',
    specs: { type: 'ip-proprietary', side: 'indoor-monitor', monitorInches: 7, poe: true },
  },

  {
    id: 'hikvision-ds-kh8520-wte1',
    manufacturer: 'Hikvision',
    model: 'DS-KH8520-WTE1',
    category: 'intercom',
    tagline: '10.1-inch IP indoor station with Wi-Fi',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/en/products/Video-Intercom-Products/IP-Series/Pro-Series/ds-kh8520-wte1/',
    specs: { type: 'ip-proprietary', side: 'indoor-monitor', monitorInches: 10, poe: true },
  },

  {
    id: 'hikvision-ds-kh9310-wte1-b',
    manufacturer: 'Hikvision',
    model: 'DS-KH9310-WTE1(B)',
    category: 'intercom',
    tagline: '7-inch all-in-one Ultra-series IP indoor station',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/en/products/Video-Intercom-Products/IP-Series/ultra-series/ds-kh9310-wte1-b-/',
    specs: { type: 'ip-proprietary', side: 'indoor-monitor', monitorInches: 7, poe: true },
  },

  {
    id: 'hikvision-ds-kh9510-wte1-b',
    manufacturer: 'Hikvision',
    model: 'DS-KH9510-WTE1(B)',
    category: 'intercom',
    tagline: '10.1-inch Android IP indoor station, Wi-Fi, Hik-Connect built-in',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/en/products/Video-Intercom-Products/IP-Series/ultra-series/ds-kh9510-wte1-b-/',
    specs: { type: 'ip-proprietary', side: 'indoor-monitor', monitorInches: 10, poe: true },
  },

  // ─────────────────────────────────────────────────────────────
  // Outdoor stations — DS-KD modular + DS-KV villa
  // ─────────────────────────────────────────────────────────────

  {
    id: 'hikvision-ds-kd8003-ime1-b',
    manufacturer: 'Hikvision',
    model: 'DS-KD8003-IME1(B)',
    category: 'intercom',
    tagline: 'Modular IP door station main unit, 2 MP fisheye, PoE, IP65',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/en/products/Video-Intercom-Products/IP-Series/Pro-Series/DS-KD8003-IME1/',
    specs: {
      type: 'ip-proprietary',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 1,
      ipRating: 'IP65',
      poe: true,
    },
  },

  {
    id: 'hikvision-ds-kd8003-ime1-b-surface',
    manufacturer: 'Hikvision',
    model: 'DS-KD8003-IME1(B)/Surface',
    category: 'intercom',
    tagline: 'Surface-mount modular IP door station, 2 MP, PoE, IP65',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/mena-en/products/Video-Intercom-Products/IP-Series/Pro-Series/DS-KD8003-IME1-Surface/',
    specs: {
      type: 'ip-proprietary',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 1,
      ipRating: 'IP65',
      poe: true,
    },
  },

  {
    id: 'hikvision-ds-kv6113-wpe1-c',
    manufacturer: 'Hikvision',
    model: 'DS-KV6113-WPE1(C)',
    category: 'intercom',
    tagline: 'Plastic villa door station, 2 MP, Wi-Fi, PoE, IP65',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/en/products/Video-Intercom-Products/IP-Series/Pro-Series/ds-kv6113-wpe1-c-/',
    specs: {
      type: 'ip-proprietary',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 1,
      ipRating: 'IP65',
      poe: true,
    },
  },

  {
    id: 'hikvision-ds-kv6113-wpe1-b',
    manufacturer: 'Hikvision',
    model: 'DS-KV6113-WPE1(B)',
    category: 'intercom',
    tagline: 'Villa door station, 2 MP, Wi-Fi, PoE, IP65',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/en/products/Video-Intercom-Products/IP-Series/Pro-Series/ds-kv6113-wpe1-b-/',
    specs: {
      type: 'ip-proprietary',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 1,
      ipRating: 'IP65',
      poe: true,
    },
  },

  {
    id: 'hikvision-ds-kv8113-wme1',
    manufacturer: 'Hikvision',
    model: 'DS-KV8113-WME1',
    category: 'intercom',
    tagline: 'Metal 1-button villa door station, 2 MP, Mifare reader, PoE, IP65/IK08',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/en/products/Video-Intercom-Products/IP-Series/Pro-Series/ds-kv8113-wme1/',
    specs: {
      type: 'ip-proprietary',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 1,
      ipRating: 'IP65',
      poe: true,
      accessControl: ['mifare'],
    },
  },

  {
    id: 'hikvision-ds-kv8113-wme1-b',
    manufacturer: 'Hikvision',
    model: 'DS-KV8113-WME1(B)',
    category: 'intercom',
    tagline: 'Metal 1-button villa door station with Mifare reader, PoE, IP65/IK08',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/en/products/Video-Intercom-Products/IP-Series/Pro-Series/ds-kv8113-wme1-b-/',
    specs: {
      type: 'ip-proprietary',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 1,
      ipRating: 'IP65',
      poe: true,
      accessControl: ['mifare'],
    },
  },

  {
    id: 'hikvision-ds-kv8213-wme1-c',
    manufacturer: 'Hikvision',
    model: 'DS-KV8213-WME1(C)',
    category: 'intercom',
    tagline: 'Metal 2-button villa door station, 2 MP, Mifare, PoE, IP65',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/en/products/Video-Intercom-Products/IP-Series/Pro-Series/ds-kv8213-wme1-b-/',
    specs: {
      type: 'ip-proprietary',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 2,
      ipRating: 'IP65',
      poe: true,
      accessControl: ['mifare'],
    },
  },

  {
    id: 'hikvision-ds-kv8413-wme1-c',
    manufacturer: 'Hikvision',
    model: 'DS-KV8413-WME1(C)',
    category: 'intercom',
    tagline: 'Metal 4-button villa door station with keypad and Mifare, 2 MP, PoE, IP65/IK08',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/en/products/Video-Intercom-Products/IP-Series/Pro-Series/ds-kv8413-wme1-c-/',
    specs: {
      type: 'ip-proprietary',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 4,
      ipRating: 'IP65',
      poe: true,
      accessControl: ['mifare', 'keypad'],
    },
  },
];
